import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, openIncidentStream, readIncidentEvents, type IncidentWire } from '../../api/client'

/**
 * Keeps the alert pages current: listens to the relay's stream of incident changes and refreshes
 * what shows incidents as each arrives, instead of every page polling. Mounted once, in the header.
 *
 * A dropped stream reconnects after a pause that grows to a minute; a refusal (no `device.view`
 * anywhere) stops it, since asking again will be refused again. A change missed while disconnected
 * is caught up by refreshing on reconnect.
 */
export function useIncidentStream(enabled: boolean, onIncident?: (incident: IncidentWire) => void) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled) return
    const stop = new AbortController()
    let delay = 2_000

    const refresh = (id?: string) => {
      void queryClient.invalidateQueries({ queryKey: ['incidents'] })
      void queryClient.invalidateQueries({ queryKey: ['incident-summary'] })
      if (id) void queryClient.invalidateQueries({ queryKey: ['incident', id] })
    }

    const run = async () => {
      while (!stop.signal.aborted) {
        try {
          const body = await openIncidentStream(stop.signal)
          if (!body) return
          delay = 2_000
          refresh()
          for await (const incident of readIncidentEvents(body)) {
            refresh(incident.id)
            onIncident?.(incident)
          }
        } catch (error) {
          if (stop.signal.aborted) return
          if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return
        }
        await new Promise((resolve) => setTimeout(resolve, delay))
        delay = Math.min(delay * 2, 60_000)
      }
    }
    void run()
    return () => stop.abort()
    // onIncident is read when an event arrives; a new function each render must not reopen the stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, queryClient])
}
